import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { SmartAgentConfig } from '../types';
import App from '../App';
import { ToastProvider } from '../components/ToastContext';

export class SmartAgentWebComponent extends HTMLElement {
  private root: Root | null = null;
  private container: HTMLDivElement | null = null;
  private cssLoaded: boolean = false;
  private styleSheet: HTMLStyleElement | null = null;
  private _onKeyDown: ((e: KeyboardEvent) => void) | null = null;

  static get observedAttributes() {
    return [
      'api-key',
      'company-name',
      'theme',
      'primary-color',
      'position',
      'debug',
      'agent-name'
    ];
  }

  constructor() {
    super();

    // Create shadow DOM for style isolation
    const shadow = this.attachShadow({ mode: 'open' });

    // Inject animation / utility styles first (idempotent)
    this.injectStyles();

    // Create container for React app
    this.container = document.createElement('div');
    this.container.style.cssText = `
      all: initial;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 2147483647;
      visibility: hidden; /* hide until CSS is loaded */
    `;
    shadow.appendChild(this.container);

    // Inject compiled Tailwind CSS into shadow root
    const curScript =
      document.currentScript ||
      (() => {
        const scripts = document.getElementsByTagName('script');
        return scripts[scripts.length - 1];
      })();
    const base =
      curScript && (curScript as HTMLScriptElement).src
        ? (curScript as HTMLScriptElement).src.replace(/\/[^\/]+$/, '/')
        : './';

    fetch(base + 'style.css')
      .then((r) => r.text())
      .then((css) => {
        const styleEl = document.createElement('style');
        styleEl.textContent = css;
        // append compiled css AFTER the animation styles so authors can override if needed
        this.shadowRoot!.appendChild(styleEl);

        // CSS loaded — show container and render React
        this.cssLoaded = true;
        if (this.container) {
          this.container.style.visibility = 'visible';
        }
        this.render();
      })
      .catch(() => {
        console.warn('[SmartAgent] Failed to load styles');
        this.cssLoaded = true; // fallback
        this.render();
      });
  }

  connectedCallback() {
    // Set up space key override handler
    this._onKeyDown = (e: KeyboardEvent) => {
      // Space detection (adjust as needed)
      const isSpace = e.code === 'Space' || e.key === ' ' || e.keyCode === 32;
      if (!isSpace) return;

      // If the focused element is inside this widget (including shadowRoot), stop propagation
      const active = document.activeElement;
      const focusedInside = this.contains(active) ||
        (this.shadowRoot && this.shadowRoot.contains(active));

      if (focusedInside) {
        // Stop other page-level handlers from seeing the event
        e.stopPropagation();
        // DO NOT call e.preventDefault() — allow browser to insert the space into the input
      }
    };

    // capture = true so this runs before many non-capture handlers
    window.addEventListener('keydown', this._onKeyDown, true);

    if (this.cssLoaded) {
      this.render();
    }
  }

  disconnectedCallback() {
    // Clean up space key handler
    if (this._onKeyDown) {
      window.removeEventListener('keydown', this._onKeyDown, true);
      this._onKeyDown = null;
    }

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    // optional: remove injected style to free memory (if you ever recreate)
    if (this.styleSheet && this.styleSheet.parentNode) {
      this.styleSheet.parentNode.removeChild(this.styleSheet);
      this.styleSheet = null;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue && this.cssLoaded) {
      this.render();
    }
  }

  private getConfig(): SmartAgentConfig {
    return {
      apiKey: this.getAttribute('api-key') || '',
      companyName: this.getAttribute('company-name') || '',
      theme: (this.getAttribute('theme') as 'light' | 'dark') || 'dark',
      primaryColor: this.getAttribute('primary-color') || '#2563eb',
      position: (this.getAttribute('position') as any) || 'bottom-right',
      agentName: this.getAttribute('agent-name') || 'Lisa',
      debug: this.hasAttribute('debug'),
    };
  }

  private render() {
    if (!this.container || !this.cssLoaded) return;

    const config = this.getConfig();

    if (!config.apiKey) {
      console.error(
        '[SmartAgent] API key is required. Set the api-key attribute.'
      );
      this.container.innerHTML =
        '<div style="position:fixed;bottom:20px;right:20px;background:#ef4444;color:white;padding:10px;border-radius:5px;z-index:9999;">SmartAgent: API key required</div>';
      return;
    }

    if (!config.companyName || config.companyName.trim() === '') {
      console.error('[SmartAgent] Company name is required. Set the company-name attribute.');
      this.container.innerHTML =
        '<div style="position:fixed;bottom:20px;right:20px;background:#ef4444;color:white;padding:10px;border-radius:5px;z-index:9999;">SmartAgent: Company name required</div>';
      return;
    }

    if (!this.root) {
      this.root = createRoot(this.container);
    }

    // Mount React App directly (hook provides a global store fallback)
    this.root.render(
      React.createElement(
        ToastProvider,
        null,
        React.createElement(App, {
          initialTheme: config.theme ?? 'dark',
          initialColor: config.primaryColor ?? '#2563eb',
          apiKey: config.apiKey,
          companyName: config.companyName,
          agentName: config.agentName ?? 'Ben',
        })
      )
    );
  }

  // inject animation + helper styles into shadow root (idempotent)
  private injectStyles() {
    if (this.shadowRoot!.querySelector('#smart-agent-animations')) return;

    this.styleSheet = document.createElement('style');
    this.styleSheet.id = 'smart-agent-animations';
    this.styleSheet.textContent = `
      /* Animation keyframes */
      @keyframes smart-agent-float {
        0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.3; }
        33% { transform: translateY(-15px) rotate(2deg); opacity: 0.25; }
        66% { transform: translateY(8px) rotate(-2deg); opacity: 0.35; }
      }

      @keyframes smart-agent-fade-in-up {
        0% { opacity: 0; transform: translateY(30px) scale(0.95); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }

      /* Utility animation classes */
      .smart-agent-animate-in {
        animation: smart-agent-fade-in-up 0.8s ease-out forwards;
      }

      .smart-agent-floating {
        animation: smart-agent-float 3.6s ease-in-out infinite;
      }

      /* small useful defaults for container children */
      .smart-agent-root {
        pointer-events: auto; /* allow interactions inside the widget */
      }

      /* Make sure the host inherits desired defaults */
      :host {
        --smart-agent-z: 2147483647;
        display: block;
      }
    `;

    this.shadowRoot!.appendChild(this.styleSheet);
  }

  // Public API methods
  public open() {
    this.dispatchEvent(new CustomEvent('smart-agent-open'));
  }

  public close() {
    this.dispatchEvent(new CustomEvent('smart-agent-close'));
  }

  public sendMessage(message: string) {
    this.dispatchEvent(
      new CustomEvent('smart-agent-send-message', {
        detail: { message },
      })
    );
  }
}